/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import { createSlice } from '@reduxjs/toolkit';
import { log } from '@insticator/insticator-ui'
// thunks
import { 
    fetchCommentingConfigV2,
    fetchCommentingV2DisplayConfig,
    fetchEmojisCategories,
    updateAdminConfig,
    updateAdsConfig,
    updateAdvancedConfig,
    updateCommentingDisplayConfig,
    updateDisplayConfig,
    updateGeneralConfig
} from '../thunks/commenting'
import { updateDisplayConfigV2 } from 'state/thunks/api/comment';

/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2 SLICE : REDUCER ~~~~~~~~~~ ========== */

const initialAdsConfig = {
    isUpdating: false,
    desktopConfig: {
        showAdsBeforeCommentingUnit: false,
        showAdsAfterCommentingUnit: false,
        showAdsAfterFirstComment: false,
        showAdsAfterLoadMoreButton: false,
        showAdsBetweenRootComments: false,
        adsCountBetweenRootComments: 25,
        showAdsBetweenReplies: false,
        adsCountBetweenReplies:5
    },
    mobileConfig: {
        showAdsBeforeCommentingUnit: false,
        showAdsAfterCommentingUnit: false,
        showAdsAfterFirstComment: false,
        showAdsAfterLoadMoreButton: false,
        showAdsBetweenRootComments: false,
        adsCountBetweenRootComments: 5,
        showAdsBetweenReplies: false,
        adsCountBetweenReplies:1
    }
}

const initialDisplayConfig = {
    status: 'initial',
    isUpdating: false,
    config: {
      uuid: "",
      commentingModuleStyle: "LIGHT",
      themeExtension: "DEFAULT",
      avatarPack: "DEFAULT",
      callToActionStyle: "SUBTLE",
      fontTypeface: "DECORATIVE_AND_PLAIN",
      fontSize: "SMALL",
      useSystemFont: false,
      customPrimaryColor: '#061240',
      enableMonochrome: false,
      customSecondaryColor: null,
      customBackgroundColor: '#FFFFFF',
      userBadgeStyle: 'COLORFUL', // TODO: PP Config BE PENDING
      emojiCategoryType: "THREE_D"
    },
    emojis: []
  }
const initialState = {
    migrationStatus: 'NONE',
    isConfigValid: {
        generalConfig: true,
        advancedConfig: true,
        adsConfig: true
    },
    tempCommentingUnitConfig: null,
    tempDisplayConfig: initialDisplayConfig,
    commentingUnitConfig: {
      status: 'initial',
      commentingUnitId: '',
      generalConfig: {
        isUpdating: false,
        allowReaction: false,
        allowLinks: false,
        allowRichMedia: false,
        allowVideos: false,
        allowYoutube: false,
        allowVimeo: false,
        allowImages: false,
        allowGifs: false,
        useNewestCommentsSort: false,
        useOldestCommentsSort: false,
        useMostPopularThreadsSort: false,
        defaultSort: "NEWEST",
        commentsInitialLoadLimit: 10,
        repliesInitialLoadLimit: 5,
        commentsLoadMoreLimit: 10,
        repliesLoadMoreLimit: 5,
        inputFieldPlaceholderType: "DEFAULT",
        noCommentPlaceholder: "",
        beforeFirstCommentPlaceholder: "",
        afterFirstCommentPlaceholder: "",
        
        useHeader: true,
        headerTitle: '',
        useHeaderBorder: true,
        useHeaderCounter: true,
        headerIcon: 'DOUBLE_BUBBLE_RECT_DARK' // DOUBLE_BUBBLE_RECT_DARK, SINGLE_BUBBLE_RECT_LIGHT, SINGLE_BUBBLE_ROUND_DARK, SINGLE_BUBBLE_ROUND_LIGHT, SINGLE_BUBBLE_RECT_DARK, NONE

      },
      advancedConfig: {
        useRealTimeIndicator: false,
        allowGuestCommenting: false,
        useAutoGeneratedGuestUsername: false,
        customGuestUsername: null,
        useCommentsCounter: false,
        commentsCounterIconStyle: "DOUBLE_BUBBLE_RECT_DARK",
        commentsCounterFormat: "ICON_FIRST",
        useCustomMessage: false,
        customMessageHeading: "",
        customMessageBody: `**Sample heading**\n\nThis is a sample message to showcase what's possible via this feature. You can add different formatting like **bold**, *italics*, and even [links](https://www.insticator.com/)! Simply replace all of the copy in this textbox with your message. You can preview what this message would look like in your commenting unit by clicking the “Test Page” button at the top of the page.`,
        useLoadBehindButton: false,
        loadBehindButtonTitle: "Want to view comments?",
        loadBehindButtonDescription: "No problem! Simply login below and you can view the comments on this article or leave a comment yourself.",
        loadBehindButtonText: "Login to view comments",
        useStickyHeader: false
      },
      adminConfig: {
        commentVolume: "LOW",
        useLinkDisqusAccount: false
      },
      adsConfig: initialAdsConfig,
      displayConfig: initialAdsConfig,
      emojiConfigs: []
    },
    displayConfig: initialDisplayConfig,
    emojiCategories: []
};

const formatImportedHexColors = (hexColor, isPrimary = false) => {
    const hex = hexColor.replace(/^#/, '');
    // Check if the string is either 3 or 6 characters long and contains only hexadecimal digits
    const isValid = /^[0-9A-Fa-f]{6}$|^[0-9A-Fa-f]{3}$/.test(hex);
    // Only format if hex color is imported
    if(isValid && hexColor && hexColor.length === 6) return `#${hexColor}`
    // if hex color is empty, return default colors
    if(!hexColor) return isPrimary ? '#061240' : '#FFFFFF'
    // otherwise just return it
    return hexColor
}

export const commentingSlice = createSlice({
    name: 'commentingConfigV2',
    initialState,
    reducers: {
        setTempCommentingUnitConfig: (state, action) => {
            const { configName, data } = action.payload
            log('config name, data', configName, data)
            state.tempCommentingUnitConfig[configName] = data
        },
        setTempDisplayConfig: (state, action) => {
            // TODO: emojis
            state.tempDisplayConfig.config = action.payload
        },
        setTempDisplayConfigEmojis: (state, action) => {
            state.tempDisplayConfig.emojis = action.payload
        },
        revertConfigs: (state, action) => {
            const { configName } = action.payload
            state.tempCommentingUnitConfig[configName] = state.commentingUnitConfig[configName]
        },
        setCommentingConfigSectionValid: (state, action) => {
            const { configName, isValid } = action.payload
            state.isConfigValid[configName] = isValid
        }
    },
    extraReducers: builder => {
      builder
        // fetch commenting config
        .addCase(fetchCommentingConfigV2.pending, (state, action) => {
            state.commentingUnitConfig = {...initialState.commentingUnitConfig, status: 'pending'}
            state.tempCommentingUnitConfig = state.commentingUnitConfig
        })
        .addCase(fetchCommentingConfigV2.fulfilled, (state, action) => {
            state.commentingUnitConfig = {...action.payload, status: 'fulfilled'}
            state.tempCommentingUnitConfig = state.commentingUnitConfig
            // TODO: Fallback to FE when there's no ads config yet on the API response. Remove this once it is included
            if (!action.payload.adsConfig || !state.commentingUnitConfig.adsConfig) {
                state.commentingUnitConfig = {...state.commentingUnitConfig, adsConfig: initialAdsConfig}
                state.tempCommentingUnitConfig = state.commentingUnitConfig
            }
        })
        .addCase(fetchCommentingConfigV2.rejected, (state, action) => {
            state.commentingUnitConfig = {...initialState.commentingUnitConfig, status: 'rejected'}
            state.tempCommentingUnitConfig = state.commentingUnitConfig
        })   

        // fetch commenting display config
        .addCase(fetchCommentingV2DisplayConfig.pending, (state, action) => {
            state.displayConfig.status = 'pending'
            state.tempDisplayConfig = state.displayConfig
        })
        .addCase(fetchCommentingV2DisplayConfig.fulfilled, (state, action) => {
            if(action.payload) {
                state.displayConfig = action.payload
                const { emojis, config } = action.payload
                const displayConfigResponse = {
                    ...action.payload,
                    config: {
                        ...config,
                        customPrimaryColor: formatImportedHexColors(config.customPrimaryColor),
                        customBackgroundColor: formatImportedHexColors(config.customBackgroundColor)
                    }
                }
                state.displayConfig = displayConfigResponse
                let emojiCategoryType = 'THREE_D'
                if(emojis.length) 
                    emojiCategoryType = (emojis.length ? emojis.map(emoji => emoji.emojiCategory.emojiCategoryType)?.pop() : 'THREE_D')
                state.displayConfig.emojiCategoryType = emojiCategoryType
                state.tempDisplayConfig = displayConfigResponse
                state.tempDisplayConfig.emojiCategoryType = emojiCategoryType

            }
            state.displayConfig.status = 'fulfilled'
        })
        .addCase(fetchCommentingV2DisplayConfig.rejected, (state, action) => {
            state.displayConfig.status = 'rejected'
            state.tempDisplayConfig = state.displayConfig
        })

        // fetch emoji categories
        .addCase(fetchEmojisCategories.pending, (state, action) => {
            state.emojiCategories = []
        })
        .addCase(fetchEmojisCategories.fulfilled, (state, action) => {
            if(action.payload) state.emojiCategories = action.payload
        })
        
        // update display config config
        .addCase(updateDisplayConfig.pending, (state, action) => {
            state.displayConfig.isUpdating = true
        })
        .addCase(updateDisplayConfig.fulfilled, (state, action) => {
            if(action.payload) {
                const { emojis, config } = action.payload
                state.displayConfig.config = config
                state.displayConfig.emojis = emojis
                // TODO: Add emojis updates here 
            }
            state.displayConfig.isUpdating = false
            state.tempDisplayConfig = state.displayConfig
        })
        .addCase(updateDisplayConfig.rejected, (state, action) => {
            state.displayConfig.isUpdating = false
        })    

        // update display config v2
        .addCase(updateCommentingDisplayConfig.pending, (state, action) => {
            state.commentingUnitConfig.displayConfig.isUpdating = true
        })
        .addCase(updateCommentingDisplayConfig.fulfilled, (state, action) => {
            if(action.payload) {
                state.commentingUnitConfig.displayConfig = action.payload.displayConfig
                state.tempCommentingUnitConfig.displayConfig = action.payload.displayConfig

                state.commentingUnitConfig.emojiConfigs = action.payload.emojiConfigs
                state.tempCommentingUnitConfig.emojiConfigs = action.payload.emojiConfigs
            }
            state.commentingUnitConfig.displayConfig.isUpdating = false
        })
        .addCase(updateCommentingDisplayConfig.rejected, (state, action) => {
            state.commentingUnitConfig.displayConfig.isUpdating = false
        })   

        
        // update general config
        .addCase(updateGeneralConfig.pending, (state, action) => {
            state.commentingUnitConfig.generalConfig.isUpdating = true
        })
        .addCase(updateGeneralConfig.fulfilled, (state, action) => {
            if(action.payload) {
                state.commentingUnitConfig.generalConfig = action.payload
                state.tempCommentingUnitConfig.generalConfig = action.payload
            }
            state.commentingUnitConfig.generalConfig.isUpdating = false
        })
        .addCase(updateGeneralConfig.rejected, (state, action) => {
            state.commentingUnitConfig.generalConfig.isUpdating = false
        })    
        
         // update advanced config
         .addCase(updateAdvancedConfig.pending, (state, action) => {
            state.commentingUnitConfig.advancedConfig.isUpdating = true
        })
        .addCase(updateAdvancedConfig.fulfilled, (state, action) => {
            if(action.payload) {
                state.commentingUnitConfig.advancedConfig = action.payload
                state.tempCommentingUnitConfig.advancedConfig = action.payload
            }
            state.commentingUnitConfig.advancedConfig.isUpdating = false
        })
        .addCase(updateAdvancedConfig.rejected, (state, action) => {
            state.commentingUnitConfig.advancedConfig.isUpdating = false
        })   

        // update admin config
        .addCase(updateAdminConfig.pending, (state, action) => {
            state.commentingUnitConfig.adminConfig.isUpdating = true
        })
        .addCase(updateAdminConfig.fulfilled, (state, action) => {
            if(action.payload) {
                state.commentingUnitConfig.adminConfig = action.payload
                state.tempCommentingUnitConfig.adminConfig = action.payload
            }
            state.commentingUnitConfig.adminConfig.isUpdating = false
        })
        .addCase(updateAdminConfig.rejected, (state, action) => {
            state.commentingUnitConfig.adminConfig.isUpdating = false
        })   

        // update ads config
        .addCase(updateAdsConfig.pending, (state, action) => {
            state.commentingUnitConfig.adsConfig.isUpdating = true
        })
        .addCase(updateAdsConfig.fulfilled, (state, action) => {
            if(action.payload) {
                state.commentingUnitConfig.adsConfig = action.payload
                state.tempCommentingUnitConfig.adsConfig = action.payload
            }
            state.commentingUnitConfig.adsConfig.isUpdating = false
        })
        .addCase(updateAdsConfig.rejected, (state, action) => {
            state.commentingUnitConfig.adsConfig.isUpdating = false
        })   
    }
});

export const { setTempCommentingUnitConfig, setTempDisplayConfig, setTempDisplayConfigEmojis, revertConfigs, setCommentingConfigSectionValid } = commentingSlice.actions
export default commentingSlice.reducer;
